/**
 * GraphQL queries and hooks — service pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type { WidgetTagFilter } from '../../types/preferences';
import type { Service, ServiceConnection, ServiceFilter } from '../types/service';

/**
 * Fragment for Service fields
 */
export const SERVICE_FIELDS = gql`
  fragment ServiceFields on Service {
    entityId
    entityType
    name
    shortName
    serviceType
    teamId
    lifecycleStatus
    tier
    repo
    url
    description
    tags
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
    _extensionData
  }
`;

/**
 * Query to get a single service by ID (with technologies for detail page)
 */
export const GET_SERVICE = gql`
  ${SERVICE_FIELDS}
  query GetService($id: ID!) {
    service(id: $id) {
      ...ServiceFields
      technologies {
        entityId
        name
        lifecycleStage
        supplierId
        version
      }
      technologyCount
      technologyAssignments {
        serviceId
        technologyId
        technology {
          entityId
          name
          lifecycleStage
          supplierId
          version
        }
        metadata {
          notes
          versionId
          version {
            entityId
            versionLabel
            status
          }
        }
      }
    }
  }
`;

/**
 * Query to list services with server-side filtering, sorting, and offset pagination.
 * Pass page/pageSize/sortField/sortDirection for server-sort mode;
 * omit them to fall back to legacy cursor-based mode (first/after).
 */
export const LIST_SERVICES = gql`
  ${SERVICE_FIELDS}
  query ListServices(
    $filter: ServiceFilter
    $first: Int
    $after: String
    $page: Int
    $pageSize: Int
    $sortField: String
    $sortDirection: String
  ) {
    services(
      filter: $filter
      first: $first
      after: $after
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      edges {
        node {
          ...ServiceFields
          technologyCount
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_SERVICE_STATS = gql`
  query GetServiceStats($filter: ServiceFilter) {
    serviceStats(filter: $filter) {
      total
      byStatus
      byTier
    }
  }
`;

/**
 * Query to list services by team
 */
export const LIST_SERVICES_BY_TEAM = gql`
  ${SERVICE_FIELDS}
  query ListServicesByTeam($teamId: ID!, $first: Int, $after: String) {
    servicesByTeam(teamId: $teamId, first: $first, after: $after) {
      edges {
        node {
          ...ServiceFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Query to list services owned by the current user (created by them or belonging
 * to a team they are a member of)
 */
export const MY_SERVICES = gql`
  ${SERVICE_FIELDS}
  query MyServices(
    $first: Int
    $after: String
    $tagFilters: [WidgetTagFilter!]
    $filterOperator: WidgetTagFilterOperator
  ) {
    myServices(
      first: $first
      after: $after
      tagFilters: $tagFilters
      filterOperator: $filterOperator
    ) {
      edges {
        node {
          ...ServiceFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

/**
 * Hook to get a single service
 */
export function useService(id: string): QueryResult<{ service: Service | null }> {
  return useQuery(GET_SERVICE, {
    variables: { id },
    skip: !id,
  });
}

/**
 * Hook to list services with optional filtering
 */
export interface UseServicesOptions {
  filter?: ServiceFilter;
  /** Legacy cursor-based: fetch at most this many records. Use for non-list contexts. */
  first?: number;
  after?: string;
  /** Server-sort+offset mode: current page (1-based). */
  page?: number;
  /** Server-sort+offset mode: rows per page. */
  pageSize?: number;
  /** Server-sort+offset mode: field to sort by. */
  sortField?: string;
  /** Server-sort+offset mode: 'asc' | 'desc'. */
  sortDirection?: string | null;
}

export function useServices(
  filterOrFirst?: ServiceFilter | number,
  first?: number
): QueryResult<{ services: ServiceConnection }>;
export function useServices(
  options: UseServicesOptions
): QueryResult<{ services: ServiceConnection }>;
export function useServices(
  filterOrFirstOrOptions?: ServiceFilter | number | UseServicesOptions,
  first: number = 50
): QueryResult<{ services: ServiceConnection }> {
  const variables = (() => {
    if (filterOrFirstOrOptions === undefined || filterOrFirstOrOptions === null) {
      return { first };
    }
    if (typeof filterOrFirstOrOptions === 'number') {
      return { first: filterOrFirstOrOptions };
    }
    if (
      'page' in filterOrFirstOrOptions ||
      'pageSize' in filterOrFirstOrOptions ||
      'sortField' in filterOrFirstOrOptions ||
      'sortDirection' in filterOrFirstOrOptions ||
      'after' in filterOrFirstOrOptions
    ) {
      const opts = filterOrFirstOrOptions as UseServicesOptions;
      return {
        filter: opts.filter,
        first: opts.first,
        after: opts.after,
        page: opts.page,
        pageSize: opts.pageSize,
        sortField: opts.sortField,
        sortDirection: opts.sortDirection,
      };
    }
    // Legacy: called as useServices(filter, first)
    return { filter: filterOrFirstOrOptions as ServiceFilter, first };
  })();

  return useQuery(LIST_SERVICES, {
    variables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export function useServiceStats(filter?: ServiceFilter): QueryResult<{
  serviceStats: {
    total: number;
    byStatus: Record<string, number>;
    byTier: Record<string, number>;
  };
}> {
  return useQuery(GET_SERVICE_STATS, {
    variables: { filter },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Hook to list services by team
 */
export function useServicesByTeam(
  teamId: string,
  first: number = 50
): QueryResult<{ servicesByTeam: ServiceConnection }> {
  return useQuery(LIST_SERVICES_BY_TEAM, {
    variables: { teamId, first },
    skip: !teamId,
  });
}

/**
 * Options for {@link useMyServices}. `tagFilters`/`filterOperator` scope the
 * "My Services" dashboard widget by tag/category (issue #489).
 */
export interface UseMyServicesOptions {
  first?: number;
  tagFilters?: WidgetTagFilter[];
  filterOperator?: 'AND' | 'OR';
}

/**
 * Hook to list services owned by the current user, optionally scoped by a
 * dashboard widget's tag filter (issue #489).
 */
export function useMyServices(
  firstOrOptions: number | UseMyServicesOptions = 1000
): QueryResult<{ myServices: ServiceConnection }> {
  const options: UseMyServicesOptions =
    typeof firstOrOptions === 'number' ? { first: firstOrOptions } : firstOrOptions;

  return useQuery(MY_SERVICES, {
    variables: {
      first: options.first ?? 1000,
      tagFilters: options.tagFilters,
      filterOperator: options.filterOperator,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Hook to list every service (all pages, not capped at one server page).
 *
 * Used to build the shared service->{name, teamId, teamName} resolution map
 * (issue #495) so names/teams resolve for services the viewer does not own —
 * `useMyServices` only covers owned services. See {@link useAllPages}.
 */
export function useAllServices(
  filter?: ServiceFilter,
  first: number = 250
): QueryResult<{ services: ServiceConnection }> {
  return useAllPages<{ services: ServiceConnection }>(
    LIST_SERVICES,
    (data) => data?.services,
    { filter },
    first
  );
}
